import { useCallback, useEffect, useState } from "preact/hooks";
import type { JSX } from "preact";
import { api, ApiError, type EarlyAccessRequest } from "../../api";
import { dateTime } from "../../lib/format";
import { useI18n, type Dict } from "../../lib/i18n";
import { errorCode, type PlanName } from "../../lib/plan";
import { routeHash } from "../../lib/route";
import { apiErrorText } from "../../lib/apiError";

/**
 * Platform-admin only (see components/NavRail.tsx's navKeys and the server's
 * requirePlatformAdmin): who has asked for AgentlyDesk before there is any
 * self sign-up, triaged in one place across every workspace.
 *
 * Approving is the one irreversible action here: it creates a workspace and an
 * owner, and mails a one-time link. So it asks first, and it says plainly when
 * the account was made but the e-mail was not sent — that case is fixed by
 * resending the invite, not by approving again.
 */

const STATUSES = ["new", "contacted", "invited", "rejected"] as const;
const PLANS: readonly PlanName[] = ["free", "starter", "team"];

/** The per-seat ids this product used to sell, as they are stored on older requests. */
const LEGACY_PLANS: Record<string, PlanName> = { pro: "starter", business: "team" };

/** A bare domain typed without a scheme still needs one to be a link at all. */
function websiteHref(website: string): string {
  return /^https?:\/\//i.test(website) ? website : `https://${website}`;
}

/** The plan the form starts on: what they asked for, when that is a plan we sell (or was). */
export function requestedPlan(plan: string | null): PlanName {
  const asked = (plan ?? "").toLowerCase();
  return PLANS.find((p) => p === asked) ?? LEGACY_PLANS[asked] ?? "free";
}

function approveMessage(t: Dict, err: unknown): string {
  switch (errorCode(err)) {
    case "smtp_required":
      return t.earlyAccess.errSmtp;
    case "already_operator":
      return t.earlyAccess.errAlreadyOperator;
    case "not_approvable":
      return t.earlyAccess.errNotApprovable;
    case "slug_conflict":
      return t.earlyAccess.errSlugConflict;
    case "mail_failed":
      return t.earlyAccess.errMailFailed;
    default:
      return apiErrorText(err, t, t.earlyAccess.approveFailed);
  }
}

type RowNote = { tone: "ok" | "error"; text: string; done?: boolean; resendWorkspaceId?: number };

export function EarlyAccessView(): JSX.Element {
  const { t: all, lang } = useI18n();
  const t = all.earlyAccess;
  const [status, setStatus] = useState<"" | EarlyAccessRequest["status"]>("");
  const [requests, setRequests] = useState<EarlyAccessRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [rowErrors, setRowErrors] = useState<Record<number, string>>({});
  const [notes, setNotes] = useState<Record<number, RowNote>>({});
  const [form, setForm] = useState<{ id: number; plan: PlanName } | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.earlyAccess(status || undefined);
      setRequests(res.requests);
    } catch {
      setError(t.loadFailed);
    } finally {
      setLoading(false);
    }
  }, [status, t.loadFailed]);

  useEffect(() => {
    void load();
  }, [load]);

  /** Optimistic: the row updates at once, and rolls back with an inline error if the server refuses it. */
  const changeStatus = useCallback(
    (id: number, next: EarlyAccessRequest["status"]) => {
      const previous = requests.find((r) => r.id === id)?.status;
      if (previous === undefined) return;
      setRequests((current) => current.map((r) => (r.id === id ? { ...r, status: next } : r)));
      setRowErrors(({ [id]: _drop, ...rest }) => rest);
      api.updateEarlyAccess(id, next).catch(() => {
        setRequests((current) => current.map((r) => (r.id === id ? { ...r, status: previous } : r)));
        setRowErrors((current) => ({ ...current, [id]: t.updateFailed }));
      });
    },
    [requests, t.updateFailed],
  );

  const note = (id: number, value: RowNote): void => setNotes((current) => ({ ...current, [id]: value }));

  async function approve(request: EarlyAccessRequest, plan: PlanName): Promise<void> {
    setBusy(true);
    try {
      const res = await api.approveEarlyAccess(request.id, { plan });
      setRequests((current) => current.map((r) => (r.id === request.id ? res.request : r)));
      setForm(null);
      note(request.id, { tone: "ok", text: t.approved(request.email), done: true });
    } catch (err) {
      // The account exists after a mail_failed; only the e-mail is missing.
      const workspaceId = err instanceof ApiError ? err.body["workspaceId"] : undefined;
      note(request.id, {
        tone: "error",
        text: approveMessage(all, err),
        resendWorkspaceId: typeof workspaceId === "number" ? workspaceId : undefined,
      });
    } finally {
      setBusy(false);
    }
  }

  async function resend(requestId: number, workspaceId: number, email: string): Promise<void> {
    setBusy(true);
    try {
      await api.resendInvite(workspaceId);
      note(requestId, { tone: "ok", text: t.resent(email), done: true });
    } catch (err) {
      note(requestId, { tone: "error", text: approveMessage(all, err), resendWorkspaceId: workspaceId });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div class="stats-page early-access">
      <header class="stats-head">
        <h1>{t.title}</h1>
        <div class="stats-filters">
          <select aria-label={t.filterLabel} value={status} onChange={(e) => setStatus(e.currentTarget.value as typeof status)}>
            <option value="">{t.statusAll}</option>
            {STATUSES.map((s) => (
              <option key={s} value={s}>
                {t.statuses[s]}
              </option>
            ))}
          </select>
        </div>
      </header>

      <p class="hint" style={{ margin: "-8px 0 14px" }}>
        {t.intro}
      </p>

      {error ? (
        <div class="alert error" role="alert">
          {error}
        </div>
      ) : null}

      {requests.length === 0 && !loading ? (
        <p class="hint">{t.empty}</p>
      ) : (
        <div class="table-wrap">
          <table class="data-table early-access">
            <thead>
              <tr>
                <th scope="col">{t.date}</th>
                <th scope="col">{t.name}</th>
                <th scope="col">{t.email}</th>
                <th scope="col">{t.company}</th>
                <th scope="col">{t.website}</th>
                <th scope="col">{t.teamSize}</th>
                <th scope="col">{t.plan}</th>
                <th scope="col">{t.message}</th>
                <th scope="col">{t.status}</th>
                <th scope="col">{t.approve}</th>
              </tr>
            </thead>
            <tbody>
              {requests.map((r) => (
                <tr key={r.id}>
                  <td class="nowrap">{dateTime(r.createdAt, lang)}</td>
                  <td>{r.name}</td>
                  <td>
                    <a href={`mailto:${r.email}`}>{r.email}</a>
                  </td>
                  <td>{r.company || t.none}</td>
                  <td>
                    {r.website ? (
                      <a href={websiteHref(r.website)} target="_blank" rel="noopener noreferrer">
                        {r.website}
                      </a>
                    ) : (
                      t.none
                    )}
                  </td>
                  <td>{r.teamSize || t.none}</td>
                  <td>{r.plan || t.none}</td>
                  <td>
                    {r.message ? (
                      <details>
                        <summary>{t.showMessage}</summary>
                        <p class="early-access-message">{r.message}</p>
                      </details>
                    ) : (
                      t.none
                    )}
                  </td>
                  <td>
                    <select
                      aria-label={t.status}
                      value={r.status}
                      onChange={(e) => changeStatus(r.id, e.currentTarget.value as EarlyAccessRequest["status"])}
                    >
                      {STATUSES.map((s) => (
                        <option key={s} value={s}>
                          {t.statuses[s]}
                        </option>
                      ))}
                    </select>
                    {rowErrors[r.id] ? (
                      <span class="hint block" role="alert">
                        {rowErrors[r.id]}
                      </span>
                    ) : null}
                  </td>
                  <td>
                    {form?.id === r.id ? (
                      <div class="approve-form">
                        <p class="hint">{t.approveTitle(r.company || r.name)}</p>
                        <label>
                          {t.approvePlan}
                          <select
                            value={form.plan}
                            onChange={(e) => setForm({ ...form, plan: e.currentTarget.value as PlanName })}
                          >
                            {PLANS.map((p) => (
                              <option key={p} value={p}>
                                {all.plan.names[p] ?? p}
                              </option>
                            ))}
                          </select>
                        </label>
                        <span class="hint block">{t.approveIntro}</span>
                        <div class="actions-row">
                          <button
                            class="btn sm primary"
                            disabled={busy}
                            onClick={() => void approve(r, form.plan)}
                          >
                            {busy ? t.approving : t.approveSubmit}
                          </button>
                          <button class="btn sm" disabled={busy} onClick={() => setForm(null)}>
                            {all.common.cancel}
                          </button>
                        </div>
                      </div>
                    ) : r.status === "new" || r.status === "contacted" ? (
                      <button class="btn sm" onClick={() => setForm({ id: r.id, plan: requestedPlan(r.plan) })}>
                        {t.approve}
                      </button>
                    ) : null}

                    {notes[r.id] ? (
                      <span class={`hint block${notes[r.id]!.tone === "error" ? " error" : ""}`} role="status">
                        {notes[r.id]!.text}{" "}
                        {notes[r.id]!.done ? <a href={routeHash({ view: "customers" })}>{t.toCustomers}</a> : null}
                        {notes[r.id]!.resendWorkspaceId ? (
                          <button
                            class="btn sm"
                            disabled={busy}
                            onClick={() => void resend(r.id, notes[r.id]!.resendWorkspaceId!, r.email)}
                          >
                            {t.resend}
                          </button>
                        ) : null}
                      </span>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
